import ArrowLeft16 from '@carbon/icons-react/es/arrow--left/16';
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import CollapseCategories16 from '@carbon/icons-react/es/collapse-categories/16';
import ExpandCategories16 from '@carbon/icons-react/es/expand-categories/16';
import ZoomIn16 from '@carbon/icons-react/es/zoom--in/16';
import ZoomOut16 from '@carbon/icons-react/es/zoom--out/16';
import { Dropdown, Tooltip } from 'antd';
import React, { FC, memo, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { HideForCustomer } from 'components/atoms/user-type-utils';
import { FilterBar } from 'components/molecules/filter-bar';
import { useTranslation } from 'hooks/use-translation';
import { ReactComponent as ZooomFitSvg } from './assets/zoom-fit.svg';
import { BaselineToolbar } from './baseline';
import { TimelineFilterZoomLevel } from './constants';
import { ShiftDates } from './shift-dates';

const FilterBarWrapper = styled(FilterBar)`
  margin-top: var(--spacing-01);

  ${FilterBar.Primary} {
    gap: var(--spacing-03);
  }
`;

const ButtonGroup = styled.div`
  margin-left: var(--layout-03);
`;

const _TimelineToolbar: FC<{
  currentZoomLevel: number;
  onExpandAll(): void;
  onCollapseAll(): void;
  onZoomIn(): void;
  onZoomOut(): void;
  onZoomToFit?(): void;
  onShiftPrevious(): void;
  onShiftNext(): void;
  onZoomTo(preset: string): void;
  popupContainer: React.MutableRefObject<HTMLElement>;
  selectedBaselines: number[];
  setBaseline: (index?: number) => void;
  toggleBaseline: (id: number, index: number, value: boolean) => void;
  projectId: number;
  ganttRef: any;
}> = ({
  currentZoomLevel,
  onExpandAll,
  onCollapseAll,
  onZoomIn,
  onZoomOut,
  onZoomToFit,
  onShiftPrevious,
  onShiftNext,
  onZoomTo,
  popupContainer,
  selectedBaselines,
  setBaseline,
  toggleBaseline,
  projectId,
  ganttRef,
}) => {
  const [currentFilterTextKey, setCurrentFilterKey] = useState('choose');

  useEffect(() => {
    const currentFilter = Object.keys(TimelineFilterZoomLevel).find(
      (key) => TimelineFilterZoomLevel[key] === currentZoomLevel,
    );

    if (currentFilter) {
      setCurrentFilterKey(currentFilter.toLowerCase());
    }
  }, [currentZoomLevel]);

  const menu = () => {
    return (
      <Menu>
        {Object.keys(TimelineFilterZoomLevel).map((key) => {
          return (
            <Menu.Item
              key={key}
              onClick={() => onZoomTo(TimelineFilterZoomLevel[key])}
            >
              {key}
            </Menu.Item>
          );
        })}
      </Menu>
    );
  };

  const zoomToFitMenu = (
    <Tooltip title={useTranslation('zoom_to_fit')}>
      <Button type="text" icon={<ZooomFitSvg />} onClick={onZoomToFit} />
    </Tooltip>
  );

  return (
    <FilterBarWrapper data-preview-enable="true">
      <FilterBar.Primary>
        <HideForCustomer>
          <BaselineToolbar
            selectedBaselines={selectedBaselines}
            setBaseline={setBaseline}
            toggleBaseline={toggleBaseline}
            projectId={projectId}
            ganttRef={ganttRef}
          />
          <ShiftDates
            projectId={projectId}
            ganttRef={ganttRef}
            setBaseline={setBaseline}
          />
        </HideForCustomer>
      </FilterBar.Primary>
      <FilterBar.Secondary>
        <ButtonGroup>
          <Dropdown
            overlay={menu}
            getPopupContainer={() => popupContainer.current}
          >
            <Button
              data-cy="timeline.toolbar.dropdown"
              type="default"
              size="small"
              icon={<ChevronDown16 />}
              iconPlacement="right"
            >
              <FormattedMessage id={currentFilterTextKey} />
            </Button>
          </Dropdown>
        </ButtonGroup>
        <ButtonGroup>
          <Tooltip title={useTranslation('expand_all_tasks')}>
            <Button
              data-cy="timeline.toolbar.expand_all_tasks"
              type="text"
              icon={<ExpandCategories16 />}
              onClick={onExpandAll}
            />
          </Tooltip>
          <Tooltip title={useTranslation('collapse_all_tasks')}>
            <Button
              data-cy="timeline.toolbar.collapse_all_tasks"
              type="text"
              icon={<CollapseCategories16 />}
              onClick={onCollapseAll}
            />
          </Tooltip>
          {onZoomToFit && zoomToFitMenu}
        </ButtonGroup>
        <ButtonGroup>
          <Tooltip title={useTranslation('zoom_out')}>
            <Button
              data-cy="timeline.toolbar.zoom_out"
              type="text"
              icon={<ZoomOut16 />}
              onClick={onZoomOut}
            />
          </Tooltip>
          <Tooltip title={useTranslation('zoom_in')}>
            <Button
              data-cy="timeline.toolbar.zoom_in"
              type="text"
              icon={<ZoomIn16 />}
              onClick={onZoomIn}
            />
          </Tooltip>
          <Tooltip title={useTranslation('shift_previous')}>
            <Button
              data-cy="timeline.toolbar.shift_previous"
              type="text"
              icon={<ArrowLeft16 />}
              onClick={onShiftPrevious}
            />
          </Tooltip>
          <Tooltip title={useTranslation('shift_next')}>
            <Button
              data-cy="timeline.toolbar.shift_next"
              type="text"
              icon={<ArrowRight16 />}
              onClick={onShiftNext}
            />
          </Tooltip>
        </ButtonGroup>
      </FilterBar.Secondary>
    </FilterBarWrapper>
  );
};

export const TimelineToolbar = memo(_TimelineToolbar);
